import { Trans, useLingui } from '@lingui/react/macro';
import { CheckmarkCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useEffect } from 'react';

import { ButtonClicked, ComponentRendered } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { PaymentInterval } from 'src/api/endpoints/storeApi.types';
import {
  usePaymentIntervals,
  useStripeSubscription,
  useUpdatePaymentInterval,
} from 'src/api/queries';
import { Button, Link, Modal, NotificationBox } from 'src/components';
import { Link2 } from 'src/components/Link2';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount as formatPrice } from 'src/utils/productUtils';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

export const ChangePaymentIntervalToMonthlyForm = () => {
  const { t, i18n } = useLingui();
  const subscription = useStripeSubscription();
  const sub = subscription.data;
  const paymentIntervals = usePaymentIntervals();
  const updatePaymentInterval = useUpdatePaymentInterval();

  useEffect(() => {
    tracker.trackEvent(
      new ComponentRendered({
        component_name: 'ChangePaymentIntervalToMonthlyForm',
        component_type: 'change payment interval form',
      })
    );
  }, []);

  if (!paymentIntervals.data || !sub) return null;

  const nextBillingDate = sub.paymentInformation?.nextInvoice?.dueDate
    ? formatDate(sub.paymentInformation.nextInvoice.dueDate, {
        locale: i18n.locale,
      })
    : t`No invoice available`;

  const subAmount = sub.paymentInformation?.nextInvoice?.amount;

  if (!subAmount) {
    return (
      <NotificationBox variant="error" title={t`Something went wrong`}>
        <Trans>
          No payment or invoice information available. Please try again later or
          contact support if the problem persists.
        </Trans>
      </NotificationBox>
    );
  }

  const currentPrice = formatPrice(
    subAmount.total,
    subAmount.currency,
    i18n.locale
  );
  const currentIntervalMonths = paymentIntervals.data.isBiennial ? 24 : 12;
  const currentPricePerMonth = formatPrice(
    Number((subAmount.total / currentIntervalMonths).toFixed(2)),
    subAmount.currency,
    i18n.locale
  );
  const currentPriceBillingPeriod = paymentIntervals.data.isBiennial
    ? t`/2 years`
    : t`/year`;

  const monthlyPrice = formatPrice(
    paymentIntervals.data.options.monthly.amount.total,
    paymentIntervals.data.options.monthly.amount.currency,
    i18n.locale
  );

  const confirmText = t`Switch to monthly billing`;

  const onSubmit = (text: string) => () => {
    tracker.trackEvent(
      new ButtonClicked({
        component_location:
          ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
        text,
        action: 'confirm change payment interval',
      })
    );

    updatePaymentInterval.mutate({ newInterval: PaymentInterval.MONTHLY, sub });
  };

  if (updatePaymentInterval.isSuccess) {
    // updatePaymentInterval invalidates the subscriptions query and waits for the
    // subscriptions to be updated before it resolves to success. This means that
    // when we finally get here (isSuccess), what was previously the option price
    // has now become the current price.
    return (
      <>
        <CheckmarkCircle
          data-cy="payment-interval-confirmation-icon"
          size={86}
          className="mx-auto mb-24 text-feedback-green-500"
        />
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-xs">
            <Trans>
              You&apos;ve switched to{' '}
              <span className="whitespace-nowrap">monthly billing</span>
            </Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md" className="mt-16">
            <Trans>
              You&apos;ll be charged {currentPrice} on{' '}
              <span className="font-bold whitespace-nowrap">
                {nextBillingDate}
              </span>
            </Trans>
          </Typography>
        </Modal.Description>

        <Modal.Close asChild>
          <Button
            className="mx-auto mt-24 w-full xs:w-auto"
            data-cy="close-payment-interval-confirmation-button"
          >
            <Trans>Close</Trans>
          </Button>
        </Modal.Close>
      </>
    );
  }

  if (paymentIntervals.data.isMonthly) {
    return (
      <div className="flex flex-col gap-24">
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-sm">
            <Trans>Already on monthly billing</Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans>
              You are currently on the monthly billing plan. Looking to switch
              to annual?
            </Trans>
          </Typography>
        </Modal.Description>

        <div className="flex w-full justify-center gap-8">
          <Modal.Close asChild>
            <Button variant="secondary">
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>

          <Link2
            as="button"
            variant="primary"
            to="."
            search={{
              updateBillingInterval: 'annual',
            }}
            componentLocation={
              ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
            }
          >
            <Trans>Switch to annual</Trans>
          </Link2>
        </div>
      </div>
    );
  }

  return (
    <>
      <Modal.Title asChild>
        <Typography as="h2" variant="heading-xs">
          <Trans>Switch to monthly billing</Trans>
        </Typography>
      </Modal.Title>

      <div className="my-16">
        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans>
              Pay <strong>{monthlyPrice}/month</strong> instead of
              <br />
              {currentPricePerMonth}/month{' '}
              <span className="whitespace-nowrap">
                ({currentPrice}
                {currentPriceBillingPeriod})
              </span>
            </Trans>
          </Typography>
        </Modal.Description>
      </div>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>
          Starts from{' '}
          <strong data-cy="change-payment-next-billing-date">
            {nextBillingDate}
          </strong>
        </Trans>
      </Typography>

      {updatePaymentInterval.isError && (
        <NotificationBox
          error={updatePaymentInterval.error}
          className="mt-16"
          title={t`Something went wrong`}
          data-cy="error-message"
        >
          <span className="mt-4">
            <Trans>
              Please try again. If the problem keeps happening,&nbsp;
              <Link
                inline
                to={SUPPORT_URL.CONTACT_SUPPORT}
                trackingLocation={
                  ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
                }
              >
                contact Support
              </Link>
              .
            </Trans>
          </span>
        </NotificationBox>
      )}

      <div className="mt-24 flex w-full flex-col flex-wrap justify-center gap-16 xs:flex-row">
        <Modal.Close asChild>
          <Button
            type="button"
            variant="secondary"
            className="w-full xs:w-fit"
            disabled={updatePaymentInterval.isPending}
            data-cy="cancel-change-payment-interval-button"
          >
            <Trans>Cancel</Trans>
          </Button>
        </Modal.Close>

        <Button
          onClick={onSubmit(confirmText)}
          loading={updatePaymentInterval.isPending}
          className="w-full xs:w-fit"
          data-cy="confirm-change-payment-interval-button"
          variant="primary"
        >
          <span>{confirmText}</span>
        </Button>
      </div>
    </>
  );
};
